import { z } from 'zod';

import {
  deviceIdsAtom,
  deviceKindAtom,
  pushOutModeAtom,
} from '@/features/equipment-browser/equipment-bulk-edit/state/bulk-edit-params.state';
import {
  drawerIsOpenAtom,
  drawerTypeAtom,
} from '@/features/equipment-browser/equipment-drawer-loader/state/drawer.state';
import { jotaiStore } from '@/providers/jotai/jotai-store';
import { createMigrationRouteGuard } from '@/utils/route-guards/migration-guard';

export const EQUIPMENT_DETAILS_HOST_ROUTES = ['equipment', 'map', 'process-view'] as const;

export const EQUIPMENT_DETAILS_HOST_ROUTE_IDS = [
  '/$org/$agent/equipment',
  '/$org/$agent/map',
  '/$org/$agent/process-view',
] as const;

export type EquipmentDetailsHostRoute = (typeof EQUIPMENT_DETAILS_HOST_ROUTES)[number];
export type EquipmentDetailsHostRouteId = (typeof EQUIPMENT_DETAILS_HOST_ROUTE_IDS)[number];

export const equipmentDetailsRouteSearchSchema = z.object({
  viewId: z.string().optional(),
  sectionId: z.string().optional(),
  equipmentId: z.string().optional(),
});

export type EquipmentDetailsRouteSearch = z.infer<typeof equipmentDetailsRouteSearchSchema>;

export const isEquipmentDetailsHostRouteId = (
  routeId: string | undefined
): routeId is EquipmentDetailsHostRouteId =>
  EQUIPMENT_DETAILS_HOST_ROUTE_IDS.includes(routeId as EquipmentDetailsHostRouteId);

/** Clear bulk edit state from route-level callbacks (e.g., onLeave) */
export function clearBulkEditRouteState(): void {
  const drawerType = jotaiStore.get(drawerTypeAtom);
  const drawerIsOpen = jotaiStore.get(drawerIsOpenAtom);

  // Only clear if bulk edit drawer is actually open
  if (drawerType === 'bulk-edit' && drawerIsOpen) {
    jotaiStore.set(deviceKindAtom, undefined);
    jotaiStore.set(deviceIdsAtom, undefined);
    jotaiStore.set(drawerIsOpenAtom, false);
    jotaiStore.set(drawerTypeAtom, undefined);
    jotaiStore.set(pushOutModeAtom, null);
  }
}

/** Route beforeLoad: migration guard only. Details state is route search, not Jotai. */
export const equipmentDetailsSupportingRouteBeforeLoad = (routeId: string) => {
  const migrationGuard = createMigrationRouteGuard(routeId);

  return (ctx: Parameters<typeof migrationGuard>[0]) => {
    migrationGuard(ctx);
  };
};

/**
 * Route onLeave: close bulk edit drawer when navigating away from an equipment browser route.
 */
export function equipmentDetailsSupportingRouteOnLeave(): void {
  clearBulkEditRouteState();
}

/** Shared route options for pages that host equipment-details deep links. */
export const equipmentDetailsSupportingRouteOptions = (routeId: string) => ({
  beforeLoad: equipmentDetailsSupportingRouteBeforeLoad(routeId),
  validateSearch: equipmentDetailsRouteSearchSchema,
  onLeave: equipmentDetailsSupportingRouteOnLeave,
});
